import React, { useEffect, useRef, useState } from "react";
import {
  Popper,
  Paper,
  Typography,
  Box,
  IconButton,
  ClickAwayListener,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../assets/theme";

interface ProviderWithFallbackProps {
  providerName?: string;
  fallbackOfProviderName?: string;
}

const ProviderWithFallback: React.FC<ProviderWithFallbackProps> = ({
  providerName,
  fallbackOfProviderName,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const showTimeout = useRef<NodeJS.Timeout | null>(null);
  const hideTimeout = useRef<NodeJS.Timeout | null>(null);

  const hasFallback = Boolean(fallbackOfProviderName);

  const clearTimers = () => {
    if (showTimeout.current) clearTimeout(showTimeout.current);
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    showTimeout.current = null;
    hideTimeout.current = null;
  };

  useEffect(() => clearTimers, []);

  const handleMouseEnter = () => {
    if (!hasFallback) return;
    clearTimers();
    showTimeout.current = setTimeout(() => setOpen(true), 200);
  };

  const handleMouseLeave = () => {
    clearTimers();
    hideTimeout.current = setTimeout(() => setOpen(false), 300);
  };

  const handleClose = () => {
    clearTimers();
    setOpen(false);
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: "relative",
          display: "inline-block",
          cursor: hasFallback ? "pointer" : "default",
          color: COLORS.typographyColor,
          textDecoration: hasFallback ? "underline" : "none",
          textDecorationStyle: hasFallback ? "dotted" : "none",
          textUnderlineOffset: hasFallback ? "3px" : "0",
        }}
      >
        <Typography variant="body2">{providerName ?? "-"}</Typography>
      </Box>

      <Popper
        open={open && hasFallback}
        anchorEl={anchorRef.current}
        placement="top-start"
        sx={{ zIndex: 1300 }}
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            onMouseEnter={clearTimers}
            onMouseLeave={handleClose}
            sx={{
              p: 2,
              minWidth: 300,
              maxWidth: 440,
              boxShadow: 3,
              border: "1px solid #ccc",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Fallback Routing
              </Typography>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ color: (theme) => theme.palette.grey[500], p: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <ProviderRow label="Original Provider" value={fallbackOfProviderName} />
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

const ProviderRow: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 1,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2" fontWeight="medium" sx={{ textAlign: "right" }}>
      {value ?? "-"}
    </Typography>
  </Box>
);

export default ProviderWithFallback;
